import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { BillingOverviewRecord, UserDetails } from '@bn/api-interfaces';
import { BillingOverviewRecordType, BillingType } from '@bn/shared/entities/enums';
import { ContextType, ValueType } from '@bn/shared/entities/models';
import { DateTimeFormat, DateTimeUtils } from '@bn/shared/utils';
import { DropdownMenuItem } from '@bn/web-ui/dropdown-menu';
import { ValueTypeOptions } from '@bn/web-ui/table';
import { billingOverviewRecordTypeConfig } from './billing-history-type.config';
import { BillingOverviewRecordTypeConfigModel } from './models/billing-history-type-config.model';

export class BillingUtils {
  public billingOverviewRecordTypeConfig: BillingOverviewRecordTypeConfigModel = billingOverviewRecordTypeConfig;

  public static getTitle(billingOverviewRecord: BillingOverviewRecord): string {
    const { Type, PaymentId, Timestamp } = billingOverviewRecord;
    const billingRecordDateTime = DateTimeUtils.toLocalTime(DateTimeUtils.setMonth(Timestamp, DateTimeUtils.getMonth(Timestamp) - 1));
    const billingRecordYear = billingRecordDateTime.format(DateTimeFormat.YearLong);
    const billingRecordMonth = billingRecordDateTime.format(DateTimeFormat.MonthNameLong);
    const baseText = billingOverviewRecordTypeConfig[Type as BillingOverviewRecordType]?.title ?? '';
    const paymentIdText = PaymentId ? `#${PaymentId}` : '';
    const textToBeAdded = Type === BillingOverviewRecordType.MonthlyUsage ? `${billingRecordMonth}, ${billingRecordYear}` : paymentIdText;

    return baseText.replace('%s%', textToBeAdded && textToBeAdded !== 'null' && textToBeAdded !== '#null' ? textToBeAdded : '');
  }

  public static getContextType(billingOverviewRecord: BillingOverviewRecord): ContextType {
    const { Type } = billingOverviewRecord;

    return billingOverviewRecordTypeConfig[Type as BillingOverviewRecordType]?.contextType ?? '';
  }

  public static getDownloadText(billingOverviewRecord: BillingOverviewRecord): string {
    const { DocumentDownloadUrl, DetailedDocumentDownloadUrl } = billingOverviewRecord;

    if (DetailedDocumentDownloadUrl || DocumentDownloadUrl) return 'Download Summary';

    return '';
  }

  public static getBadgeText(billingOverviewRecord: BillingOverviewRecord): string | undefined {
    const { Type } = billingOverviewRecord;

    return billingOverviewRecordTypeConfig[Type as BillingOverviewRecordType]?.badgeText ?? '';
  }
  // Note: daysInPast is used to display modals after a certain amount of days of user trial being expired
  public static isUsersTrialExpired(userDetails: UserDetails, daysInPast = 0): boolean {
    const trialEndsDate = DateTimeUtils.dayjs(userDetails.BillingFreeUntilDate);

    const comparatorDate = DateTimeUtils.dayjsNow().subtract(daysInPast, 'days');
    const isTrialExpired = trialEndsDate.diff(comparatorDate, 'ms') < 0;

    const trialNotConsumed = userDetails.FeatureFlags?.includes('monetary-trial')
      ? userDetails.TrialBalance > 0
      : userDetails.TotalBandwidthUsed < userDetails.TrialBandwidthLimit;

    return userDetails?.BillingType === BillingType.FreeTrial && isTrialExpired && trialNotConsumed;
  }

  public static getDropdownMenuItems(id: string | number, downloadableDocs: DownloadableDocType[]): DropdownMenuItem[] {
    // TODO: odd other report type icons
    return downloadableDocs.map(({ icon, name, type }) => {
      return {
        label: name,
        icon,
        action: 'download',
        id,
        documentType: type,
      };
    });
  }

  static getInvoiceValueType(size: number): ValueType {
    // Note: in case detailed document exists there are two documents
    if (size > 1) {
      return 'actions';
    }
    if (size === 1) {
      return 'link';
    }

    return 'string';
  }

  static getValueTypeOptions(downloadableDocs: DownloadableDocType[]): ValueTypeOptions {
    if (!downloadableDocs.length) return {};

    return {
      href: downloadableDocs[0]?.url, // Multiple links are handled as menu, so mostly 1 comes in here
      textNoWrap: true,
    };
  }

  static getBillingPdfUrl(docUrl: string, token: string): string {
    let url: URL;

    if (!docUrl) return '';

    try {
      url = new URL(docUrl);
      url.searchParams.append('authorization', token);
    } catch (error) {
      console.error('Invalid document URL', error);
      return '';
    }

    return url.href;
  }

  /**
   * Map a billing record to downloadable documents.
   *
   * Details at: https://bunny-net.atlassian.net/wiki/spaces/Products/pages/898105345/Account+-+Billing+history
   * Rules derived from existing logic:
   * - Returns 0..2 entries with `{ url, type }`.
   * - `type` is one of: 'Invoice' | 'Receipt' | 'Summary' | 'Refund'.
   *
   * @param record - BillingRecordModel from API
   * @param taxOnUsage - whether taxes-on-usage behavior is active (default false)
   * @returns array of document objects (possibly empty)
   */
  static getBillingDocument(record: BillingOverviewRecord, token: string, translate: TranslateService, taxOnUsage = false): DownloadableDocType[] {
    const { DocumentDownloadUrl: docUrl, DetailedDocumentDownloadUrl: detailedUrl, InvoiceAvailable: hasInvoice, Type } = record;
    const make = (url: string, type: BillingDocumentType): DownloadableDocType[] => [
      {
        name: url ? translate.instant(`DOWNLOAD.${type}`) : '',
        // TODO (later): Maybe add additional type icons?
        icon: type === 'Summary' ? 'file-spreadsheet' : 'file-invoice',
        type,
        url: url ? BillingUtils.getBillingPdfUrl(url, token) : '',
      },
    ];

    // TODO (later): Unit test candidate
    switch (Type as BillingOverviewRecordType) {
      // Top-ups: PayPal (0), Crypto (1), Credit card (2), Bank wire (6)
      case BillingOverviewRecordType.PayPal:
      case BillingOverviewRecordType.Bitcoin:
      case BillingOverviewRecordType.CreditCard:
      case BillingOverviewRecordType.BankTransfer:
        if (docUrl && !detailedUrl) {
          if (hasInvoice) return make(docUrl, 'Invoice');
          if (!hasInvoice) return make(docUrl, 'Receipt');
        }
        return [];

      // Monthly usage
      case BillingOverviewRecordType.MonthlyUsage:
        if (hasInvoice) {
          // cover case: invoice + summary
          return [...(docUrl ? make(docUrl, 'Invoice') : []), ...(detailedUrl ? make(detailedUrl, 'Summary') : [])];
        } else {
          // cover case: summary from either doc url
          return [...(docUrl ? make(docUrl, 'Summary') : []), ...(detailedUrl ? make(detailedUrl, 'Summary') : [])];
        }

      // Refund (4)
      case BillingOverviewRecordType.Refund:
        if (docUrl && !detailedUrl && !hasInvoice && taxOnUsage) return make(docUrl, 'Refund');
        return [];

      // Coupon (5), Affiliate balance (7) or any other types => no docs
      default:
        return [];
    }
  }
}

export type BillingDocumentType = 'Invoice' | 'Receipt' | 'Summary' | 'Refund';
export type DownloadableDocType = { name: string; url: string; type: BillingDocumentType; icon: IconProp };
